import React, { useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { X } from 'lucide-react';

const CreateUserModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'caller'
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.createUser(formData);
      toast.success('User created successfully');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold font-headings text-slate-900">Create User</h2>
            <p className="text-slate-600 font-body text-sm mt-1">Add a new team member</p>
          </div>
          <button
            data-testid="close-create-user-modal"
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-md transition-colors"
          >
            <X size={24} className="text-slate-600" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-700 font-body mb-2">
              Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              data-testid="create-user-name-input"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 font-body mb-2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              data-testid="create-user-email-input"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700 font-body mb-2">
              Password *
            </label>
            <input
              type="password"
              id="password"
              name="password"
              data-testid="create-user-password-input"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body"
              placeholder="••••••••"
            />
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-slate-700 font-body mb-2">
              Role *
            </label>
            <select
              id="role"
              name="role"
              data-testid="create-user-role-select"
              value={formData.role}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 font-body bg-white"
            >
              <optgroup label="Sales Calling">
                <option value="caller">Caller</option>
                <option value="manager">Manager</option>
              </optgroup>
              <optgroup label="Complaints">
                <option value="complaint_entry">Complaint Entry Agent</option>
                <option value="complaint_solver">Complaint Solver</option>
              </optgroup>
              <optgroup label="Admin">
                <option value="admin">Admin</option>
              </optgroup>
              <optgroup label="Results">
                <option value="result_agent">Result Agent</option>
              </optgroup>
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-white text-slate-900 border border-slate-200 rounded-md hover:bg-slate-50 transition-all shadow-sm font-body"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="create-user-submit-button"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-all shadow-sm font-body disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;